import { useEffect, useRef, useState } from "react";
import {
  AiIcon,
  CheckIcon,
  CrossIcon,
  PaperAirplaneIcon,
  StarsIcon,
  TrashIcon,
} from "../Icons";

import {
  useAddAiInstructionsMutation,
  useDeleteAiInstructionsMutation,
  useGetAiInstructionsQuery,
} from "../../store/apiSlices/aiAutomate";
import Popover from "./Popover";
import { parseServerDate, timeAgo } from "../../utils";
import { MentionEditorHandle } from "../Shared/MentionInput";
import { useGetContractsQuery } from "../../store/apiSlices/tradeFlow";
import AIAutomateMentionEditor from "../Shared/AIAutomateMentionEditor";

export default function TableAIAutomateBtn({ tableId }: { tableId: number }) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isPopoverOpen) return;

    const el = document.getElementById(
      "instructions-input",
    ) as HTMLTextAreaElement;

    requestAnimationFrame(() => {
      el?.focus();
    });
  }, [isPopoverOpen]);

  const toggleModal = () => {
    setIsPopoverOpen((prev) => !prev);
  };

  return (
    <div className="inline-flex justify-center">
      <button
        ref={btnRef}
        className="py-1 px-2 rounded-xl bg-custom-blue-medium hover:scale-90"
        title="AI automate"
        onClick={toggleModal}
      >
        <AiIcon />
      </button>

      <Popover
        open={isPopoverOpen}
        onClose={toggleModal}
        triggerRef={btnRef}
        className="w-[350px]"
      >
        <div className="flex justify-between items-center mb-2">
          <h2 className="inline-flex items-center gap-1 text-sm text-gray-800">
            <StarsIcon className="size-4" />
            AI Instructions
          </h2>

          <button
            onClick={toggleModal}
            className="hover:scale-95 hover:text-red-400"
          >
            <CrossIcon className="size-4" />
          </button>
        </div>
        <AiInstructionsList tableId={tableId} />
        <AiInstructionsInput tableId={tableId} />
      </Popover>
    </div>
  );
}

function AiInstructionsInput({ tableId }: { tableId: number }) {
  const editorRef = useRef<MentionEditorHandle>(null);
  const [hasContent, setHasContent] = useState(false);

  const { data: allContacts } = useGetContractsQuery({ table_id: tableId });

  const [addAiInstructions] = useAddAiInstructionsMutation();

  const onSend = async () => {
    const input = editorRef.current?.getValue() || "";

    const payload = {
      table_id: tableId,
      instructions: input,
    };

    editorRef.current?.clear();
    setHasContent(false);

    await addAiInstructions(payload);
  };

  return (
    <div className="pt-2 border-t bg-white flex items-end gap-2">
      <AIAutomateMentionEditor
        ref={editorRef}
        contacts={allContacts || []}
        documents={[]}
        milestones={[]}
        id="instructions-input"
        placeholder="Type AI Instructions..."
        onInput={() => {
          const value = editorRef.current?.getValue() || "";
          setHasContent(!!value.trim());
        }}
      />

      <button
        onClick={onSend}
        disabled={!hasContent}
        className={`rounded-lg px-4 py-2 font-medium transition-all shrink-0 ${
          hasContent
            ? "bg-custom-blue-medium hover:bg-blue-700 text-white"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }`}
      >
        <PaperAirplaneIcon />
      </button>
    </div>
  );
}

function AiInstructionsList({ tableId }: { tableId: number }) {
  const {
    data: allAiInstructions,
    isLoading,
    isError,
    refetch,
  } = useGetAiInstructionsQuery({ table_id: tableId });

  const [deleteAiInstructions] = useDeleteAiInstructionsMutation();

  const containerRef = useRef<HTMLDivElement>(null);

  // whenever list changes, scroll to bottom
  useEffect(() => {
    if (!allAiInstructions?.data) return;

    const el = containerRef.current;
    if (!el) return;

    // scroll bottom
    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [allAiInstructions?.data?.length]);

  const handleDelete = async (id: number) => {
    await deleteAiInstructions({
      ai_task_id: id,
      table_id: tableId,
    });
  };

  if (isLoading) {
    return (
      <div className="p-2 space-y-2">
        <div className="animate-pulse rounded-xl bg-gray-200 h-12 w-full"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-2">
        <p className="text-sm text-red-500">Failed to load instructions</p>
        <button onClick={refetch} className="underline text-sm">
          relaod
        </button>
      </div>
    );
  }

  const items = allAiInstructions?.data;

  if (!items || !items.length) {
    return (
      <div className="text-sm text-gray-400 p-2">No saved instructions yet</div>
    );
  }

  return (
    <div
      className="max-h-52 overflow-y-auto space-y-2 p-1 pr-2"
      ref={containerRef}
    >
      {items.map((ins) => (
        <div
          key={ins.id}
          className="relative rounded-xl bg-gray-100 border border-gray-200 text-sm p-3"
        >
          <p
            className="text-gray-700"
            dangerouslySetInnerHTML={{ __html: ins.instructions }}
          />

          <span className="text-xs text-gray-400 block mt-1">
            {ins.created_at ? timeAgo(parseServerDate(ins.created_at)) : "..."}
          </span>

          {ins.is_completed === 1 && (
            <div className="absolute bottom-2 right-2 bg-green-300 rounded-full text-white p-[2px]">
              <CheckIcon className="size-3" />
            </div>
          )}
          {!ins.is_completed && (
            <button
              onClick={() => handleDelete(ins.id)}
              className="absolute bottom-2 right-2 text-gray-400 hover:text-red-500 transition"
              aria-label="delete instruction"
            >
              <TrashIcon className="size-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
